import { type HTMLInputTypeAttribute, useId } from "react";
import styles from "./FormRow.module.css";

type CellShellProps = {
  // Data rows head their first column with a th, so form rows do too.
  isRowHeader?: boolean;
};

type FormCellProps = CellShellProps & {
  form: string;
  name: string;
  label: string;
  defaultValue?: string;
  errors?: string[];
  placeholder?: string;
  inputMode?: "numeric";
  type?: HTMLInputTypeAttribute;
  // Supplied together when another field derives from this one. Left out
  // everywhere else, which keeps the input uncontrolled and the caller free of
  // state it has no use for.
  value?: string;
  onValueChange?: (next: string) => void;
};

// The column header is this input's visible label, so it names the input too.
export const FormCell = ({
  form,
  name,
  label,
  defaultValue,
  errors,
  placeholder,
  inputMode,
  type = "text",
  isRowHeader,
  value,
  onValueChange,
}: FormCellProps) => {
  const errorId = useId();
  const hasError = errors !== undefined && errors.length > 0;
  const Cell = isRowHeader ? "th" : "td";
  const isControlled = value !== undefined && onValueChange !== undefined;

  return (
    <Cell className={styles.cell} scope={isRowHeader ? "row" : undefined}>
      <input
        className={styles.input}
        form={form}
        name={name}
        type={type}
        aria-label={label}
        aria-invalid={hasError || undefined}
        // aria-describedby rather than aria-errormessage: support for the
        // latter is still uneven across screen readers.
        aria-describedby={hasError ? errorId : undefined}
        {...(isControlled
          ? {
              value,
              onChange: (event) => onValueChange(event.target.value),
            }
          : { defaultValue })}
        placeholder={placeholder}
        inputMode={inputMode}
      />
      {hasError ? (
        <span id={errorId} className={styles.error} role="alert">
          {errors.join(" ")}
        </span>
      ) : null}
    </Cell>
  );
};

// A code is UPPER_SNAKE and can never be changed once the record exists, so
// there is exactly one moment to get it right. Rather than reject a bad shape
// after submitting, the field only ever holds a legal one: uppercase, spaces
// and hyphens folded to underscores, anything else dropped, and a leading
// character that is not a letter removed because the pattern forbids it.
export const toCodeValue = (raw: string): string =>
  raw
    .toUpperCase()
    .replace(/[\s-]+/g, "_")
    .replace(/[^A-Z0-9_]/g, "")
    .replace(/_{2,}/g, "_")
    .replace(/^[^A-Z]+/, "");

// Controlled by the row rather than by itself: the label field derives this
// one, so a single owner has to hold both.
type CodeCellProps = CellShellProps & {
  form: string;
  name: string;
  label: string;
  value: string;
  onValueChange: (next: string) => void;
  errors?: string[];
  placeholder?: string;
};

export const CodeCell = ({
  form,
  name,
  label,
  value,
  onValueChange,
  errors,
  placeholder,
  isRowHeader,
}: CodeCellProps) => {
  const errorId = useId();
  const hasError = errors !== undefined && errors.length > 0;
  const Cell = isRowHeader ? "th" : "td";

  return (
    <Cell className={styles.cell} scope={isRowHeader ? "row" : undefined}>
      <input
        className={styles.input}
        form={form}
        name={name}
        type="text"
        aria-label={label}
        aria-invalid={hasError || undefined}
        aria-describedby={hasError ? errorId : undefined}
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
        placeholder={placeholder}
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
      />
      {hasError ? (
        <span id={errorId} className={styles.error} role="alert">
          {errors.join(" ")}
        </span>
      ) : null}
    </Cell>
  );
};

type FormSelectCellProps = CellShellProps & {
  form: string;
  name: string;
  label: string;
  options: readonly string[];
  defaultValue?: string;
  errors?: string[];
};

// A closed set of values (a DB CHECK constraint) is a select, not a text box —
// the constraint is then unbreakable from the UI rather than merely validated.
export const FormSelectCell = ({
  form,
  name,
  label,
  options,
  defaultValue,
  errors,
  isRowHeader,
}: FormSelectCellProps) => {
  const errorId = useId();
  const hasError = errors !== undefined && errors.length > 0;
  const Cell = isRowHeader ? "th" : "td";

  return (
    <Cell className={styles.cell} scope={isRowHeader ? "row" : undefined}>
      <select
        // See the note on SelectField: without this an uncontrolled select
        // never moves off the option it first mounted with.
        key={defaultValue}
        className={styles.input}
        form={form}
        name={name}
        aria-label={label}
        aria-invalid={hasError || undefined}
        aria-describedby={hasError ? errorId : undefined}
        defaultValue={defaultValue}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      {hasError ? (
        <span id={errorId} className={styles.error} role="alert">
          {errors.join(" ")}
        </span>
      ) : null}
    </Cell>
  );
};

type FormCheckboxCellProps = CellShellProps & {
  form: string;
  name: string;
  label: string;
  defaultChecked?: boolean;
};

// An unchecked box posts nothing at all, so the schema behind this reads an
// absent value as false rather than expecting "off".
export const FormCheckboxCell = ({
  form,
  name,
  label,
  defaultChecked,
  isRowHeader,
}: FormCheckboxCellProps) => {
  const Cell = isRowHeader ? "th" : "td";

  return (
    <Cell className={styles.cell} scope={isRowHeader ? "row" : undefined}>
      <input
        className={styles.checkbox}
        form={form}
        name={name}
        type="checkbox"
        aria-label={label}
        defaultChecked={defaultChecked}
      />
    </Cell>
  );
};

type StaticCellProps = CellShellProps & {
  value: string;
};

// Identifiers stay put while the rest of the row is edited: other rows point at
// them by name, so renaming one would orphan those references.
export const StaticCell = ({ value, isRowHeader }: StaticCellProps) => {
  const Cell = isRowHeader ? "th" : "td";

  return (
    <Cell className={styles.cell} scope={isRowHeader ? "row" : undefined}>
      <span className={styles.static}>{value}</span>
    </Cell>
  );
};
